import { useState } from "react";
import { AddItemsPickerButton } from "../../../../shared/orderLineForm/components/addItemsPickerStyles";
import { FulfilLineGrid, productLabel } from "../FulfilLineGrid";
import {
  SectionHint,
  SectionTitle,
  SelectionPill,
  StepFooter,
  StepOptions,
  StepResult,
  StepState,
} from "../FulfilParts";
import { totalQty, trimQty } from "../fulfilHelpers";
import { useFulfilStep } from "../useFulfilStep";
import { useStepSelection } from "../useStepSelection";

const remaining = row => row.remaining_qty;

const COLUMNS = [
  { key: "line", label: "#", render: row => row.line_number },
  { key: "product", label: "Product", render: row => productLabel(row.product) },
  { key: "ordered", label: "Ordered", numeric: true, render: row => trimQty(row.quantity) },
  { key: "invoiced", label: "Invoiced", numeric: true, render: row => trimQty(row.invoiced_qty) },
  { key: "price", label: "Unit price", numeric: true, render: row => trimQty(row.unit_price) },
];

/**
 * Step 5: create one invoice for the chosen lines, as a draft to review.
 *
 * @param {{orderId: (string|number)}} props
 */
export const InvoiceStep = ({ orderId }) => {
  const { query, rows, defaults, run, saving, result, clearResult } = useFulfilStep(
    orderId,
    "invoice",
  );
  const selection = useStepSelection(rows, {
    idKey: "line_id",
    defaultQty: remaining,
    maxQty: remaining,
  });
  const [submitNow, setSubmitNow] = useState(false);

  if (result) {
    return <StepResult result={result} onAgain={clearResult} />;
  }

  const count = selection.selectedRows.length;
  const total = trimQty(totalQty(selection.checked, selection.quantities));
  const terms = [
    defaults.payment_term?.name && `Payment terms: ${defaults.payment_term.name}`,
    defaults.due_date && `Due ${defaults.due_date}`,
  ]
    .filter(Boolean)
    .join(" · ");

  const submit = () =>
    run({
      lines: selection.selectedRows.map(row => ({
        line_id: row.line_id,
        qty: selection.quantities[row.line_id],
      })),
      submit: submitNow,
    });

  return (
    <>
      <SectionTitle>Invoice</SectionTitle>
      <SectionHint>
        Creates one invoice for the lines you tick, using the customer's payment terms and each
        line's price and account. Open it afterwards to review before sending.
      </SectionHint>
      <StepState query={query} emptyText="Everything on this order has been invoiced.">
        <FulfilLineGrid
          rows={rows}
          idKey="line_id"
          columns={COLUMNS}
          selection={selection}
          qtyLabel="Invoice now"
          disabled={saving}
          ariaLabel="Lines to invoice"
        />
        <StepOptions>
          {terms ? <span className="terms">{terms}</span> : null}
          <label className="inline">
            <input
              type="checkbox"
              checked={submitNow}
              onChange={event => setSubmitNow(event.target.checked)}
            />
            Submit it for approval straight away
          </label>
        </StepOptions>
        <StepFooter pill={<SelectionPill selected={count} total={rows.length} units={total} />}>
          <AddItemsPickerButton
            type="primary"
            loading={saving}
            disabled={!count || selection.hasErrors || saving}
            onClick={submit}
          >
            {submitNow ? "Create and submit invoice" : "Create draft invoice"}
          </AddItemsPickerButton>
        </StepFooter>
      </StepState>
    </>
  );
};
